import React, { useState, useEffect, useCallback, useRef } from "react";
import { Bell, CheckCheck, X } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";

const TYPE_DOT = {
  kpi: "bg-[#00E65B]", trial_reminder: "bg-[#FFB800]", trial_expired: "bg-red-500", subscription: "bg-accent",
  import: "bg-sky-400", hazard: "bg-orange-400", integrity: "bg-red-500",
};

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const load = useCallback(() => {
    api.get("/onboarding/notifications")
      .then((r) => { setItems(r.data.notifications || []); setUnread(r.data.unread || 0); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = () => { setOpen((o) => !o); if (!open) load(); };

  const markRead = async (id) => {
    setItems((p) => p.map((n) => (n._id === id ? { ...n, read: true } : n)));
    setUnread((u) => Math.max(0, u - 1));
    try { await api.post(`/onboarding/notifications/${id}/read`); } catch { /* noop */ }
  };
  const dismiss = async (id, wasUnread) => {
    setItems((p) => p.filter((n) => n._id !== id));
    if (wasUnread) setUnread((u) => Math.max(0, u - 1));
    try { await api.delete(`/onboarding/notifications/${id}`); } catch { /* noop */ }
  };
  const markAll = async () => {
    const unreadIds = items.filter((n) => !n.read).map((n) => n._id);
    setItems((p) => p.map((n) => ({ ...n, read: true }))); setUnread(0);
    await Promise.allSettled(unreadIds.map((id) => api.post(`/onboarding/notifications/${id}/read`)));
  };

  return (
    <div className="relative" ref={ref}>
      <Button variant="ghost" size="icon" onClick={toggle} data-testid="notification-bell" aria-label="Notifications" className="relative">
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span data-testid="notification-unread-count"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </Button>
      {open && (
        <div data-testid="notification-panel"
          className="absolute right-0 mt-2 w-80 max-h-[26rem] overflow-y-auto bg-card border border-border rounded-md shadow-xl z-50">
          <div className="flex items-center justify-between px-3 py-2 border-b border-border sticky top-0 bg-card">
            <span className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Notifications</span>
            {unread > 0 && (
              <button onClick={markAll} data-testid="notification-mark-all" className="text-[10px] text-accent inline-flex items-center gap-1 hover:underline">
                <CheckCheck className="h-3 w-3" /> Mark all read
              </button>
            )}
          </div>
          {items.length === 0 ? (
            <div className="px-4 py-8 text-center text-xs text-muted-foreground">You're all caught up.</div>
          ) : (
            items.map((n) => (
              <div key={n._id} role="button" tabIndex={0} onClick={() => !n.read && markRead(n._id)} data-testid={`notification-item-${n._id}`}
                className={`group w-full text-left px-3 py-2.5 border-b border-border last:border-0 hover:bg-surface-hover transition-colors cursor-pointer ${n.read ? "opacity-60" : ""}`}>
                <div className="flex items-start gap-2">
                  <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${n.read ? "bg-muted-foreground/30" : (TYPE_DOT[n.type] || "bg-accent")}`} />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium truncate">{n.subject}</div>
                    <div className="text-[11px] text-muted-foreground line-clamp-2">{n.body}</div>
                    <div className="text-[9px] text-muted-foreground/70 font-mono mt-0.5">{(n.sent_at || "").replace("T", " ").slice(0, 16)}</div>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); dismiss(n._id, !n.read); }}
                    data-testid={`notification-dismiss-${n._id}`} aria-label="Dismiss notification"
                    className="mt-0.5 p-1 rounded text-muted-foreground/50 hover:text-foreground hover:bg-surface-hover opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
